import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  MapPin, 
  Users, 
  RefreshCw, 
  Navigation, 
  Clock, 
  User,
  Route,
  Eye,
  Trash2,
  Radio
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom icons for different roles
const vendedorIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const gestorIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const adminIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const getIconForRole = (role) => {
  switch (role) {
    case 'admin': return adminIcon;
    case 'gestor': return gestorIcon;
    default: return vendedorIcon;
  }
};

const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
};

export default function LocationTrackingPage() {
  const { api, user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userHistory, setUserHistory] = useState([]);
  const [historyDate, setHistoryDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [mapCenter, setMapCenter] = useState([39.5, -8.0]);
  const refreshInterval = useRef(null);

  useEffect(() => {
    loadLocations();
    
    // Auto-refresh every 30 seconds
    if (autoRefresh) {
      refreshInterval.current = setInterval(loadLocations, 30000);
    }
    
    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [autoRefresh]);

  const loadLocations = async () => {
    try {
      const response = await api().get('/location/team');
      setLocations(response.data);
      
      // Center map on first location if available
      if (response.data.length > 0 && !selectedUser) {
        const first = response.data[0];
        if (first.lat && first.lng) {
          setMapCenter([first.lat, first.lng]);
        }
      }
    } catch (error) {
      console.error('Error loading locations', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserHistory = async (userId) => {
    try {
      const response = await api().get(`/location/history/${userId}?date=${historyDate}`);
      setUserHistory(response.data);
      
      // Center on first point
      if (response.data.length > 0) {
        setMapCenter([response.data[0].lat, response.data[0].lng]);
      }
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const clearHistory = async (userId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar o histórico de localização deste utilizador?')) return;
    
    try {
      await api().delete(`/location/history/${userId}`);
      toast.success('Histórico eliminado');
      setUserHistory([]);
    } catch (error) {
      toast.error('Erro ao eliminar histórico');
    }
  };

  const selectUser = (loc) => {
    setSelectedUser(loc);
    setMapCenter([loc.lat, loc.lng]);
    loadUserHistory(loc.user_id);
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
    toast.info(autoRefresh ? 'Auto-refresh desativado' : 'Auto-refresh ativado (30s)');
  };

  const getHistoryPolyline = () => {
    return userHistory.map(h => [h.lat, h.lng]);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Apenas administradores podem aceder a esta página</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="location-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] flex items-center gap-2">
            <Radio className="h-6 w-6 text-amber-500" />
            Localização da Equipa
          </h1>
          <p className="text-muted-foreground">
            Acompanhe a localização dos vendedores e gestores em tempo real
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            className={autoRefresh ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            onClick={toggleAutoRefresh}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto ON' : 'Auto OFF'}
          </Button>
          <Button variant="outline" onClick={loadLocations}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Team Members List */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-[Manrope] flex items-center gap-2">
                <Users className="h-5 w-5 text-amber-500" />
                Equipa Online ({locations.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
              {locations.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nenhum membro online
                </p>
              ) : (
                locations.map((loc) => (
                  <div 
                    key={loc.user_id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedUser?.user_id === loc.user_id 
                        ? 'bg-amber-50 border-amber-300' 
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => selectUser(loc)}
                    data-testid={`user-location-${loc.user_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          loc.user_role === 'admin' ? 'bg-red-500' :
                          loc.user_role === 'gestor' ? 'bg-emerald-500' : 'bg-blue-500'
                        }`} />
                        <div>
                          <p className="font-medium">{loc.user_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {loc.user_role === 'admin' ? 'Administrador' : 
                             loc.user_role === 'gestor' ? 'Gestor' : 'Vendedor'}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {format(new Date(loc.updated_at), 'HH:mm')}
                      </Badge>
                    </div>
                    {loc.speed && loc.speed > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        <Navigation className="h-3 w-3 inline mr-1" />
                        {Math.round(loc.speed * 3.6)} km/h
                      </p>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* User History Controls */}
          {selectedUser && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-[Manrope] flex items-center gap-2">
                  <Route className="h-5 w-5 text-amber-500" />
                  Histórico: {selectedUser.user_name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={historyDate}
                    onChange={(e) => setHistoryDate(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    size="icon" 
                    variant="outline"
                    onClick={() => loadUserHistory(selectedUser.user_id)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
                
                {userHistory.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {userHistory.length} pontos registados
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Início: {format(new Date(userHistory[0]?.timestamp), 'HH:mm', { locale: pt })}
                      {' → '}
                      Fim: {format(new Date(userHistory[userHistory.length - 1]?.timestamp), 'HH:mm', { locale: pt })}
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 w-full"
                      onClick={() => clearHistory(selectedUser.user_id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Limpar Histórico
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Legend */}
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs font-medium mb-2">Legenda:</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span>Administrador</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span>Gestor</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span>Vendedor</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map */}
        <div className="lg:col-span-2">
          <Card className="h-[600px]">
            <CardContent className="p-0 h-full">
              <MapContainer
                center={mapCenter}
                zoom={10}
                style={{ height: '100%', width: '100%', borderRadius: '0.5rem' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; OpenStreetMap'
                />
                <MapController center={mapCenter} zoom={selectedUser ? 14 : 10} />
                
                {/* Current locations */}
                {locations.map((loc) => (
                  <Marker
                    key={loc.user_id}
                    position={[loc.lat, loc.lng]}
                    icon={getIconForRole(loc.user_role)}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-medium">{loc.user_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {loc.user_role === 'admin' ? 'Administrador' : 
                           loc.user_role === 'gestor' ? 'Gestor' : 'Vendedor'}
                        </p>
                        <p className="text-xs mt-1">
                          Última atualização: {format(new Date(loc.updated_at), 'HH:mm:ss')}
                        </p>
                        {loc.speed && loc.speed > 0 && (
                          <p className="text-xs">Velocidade: {Math.round(loc.speed * 3.6)} km/h</p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
                
                {/* User history polyline */}
                {userHistory.length > 1 && (
                  <Polyline
                    positions={getHistoryPolyline()}
                    color="#f59e0b"
                    weight={3}
                    opacity={0.8}
                  />
                )}
              </MapContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
